import type { ReactNode } from "react";

type EmptyStateProps = {
  icon: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
};

/**
 * Shared "nothing here" block for the public site.
 *
 * The empty states were previously one-liners with no shared treatment - the
 * course grid rendered a bare white card, the teacher grid a line of grey text
 * - so a no-results page read as a page that had failed to load. This gives
 * them one consistent shape: an icon in a tinted circle, a clear title, an
 * optional line of explanation, and an optional action.
 *
 * Uses the site's palette tokens (`primary` / `secondary`) and type scale - this is
 * a consistency pass, not a restyle.
 */
export default function EmptyState({
  icon,
  title,
  description,
  action,
}: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center rounded-[10px] border border-secondary/20 bg-white px-6 py-14 text-center shadow-sm">
      <div
        aria-hidden="true"
        className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary/10 text-secondary"
      >
        {icon}
      </div>

      <p className="mt-5 text-lg font-bold text-primary">{title}</p>

      {description ? (
        <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">
          {description}
        </p>
      ) : null}

      {action ? <div className="mt-6">{action}</div> : null}
    </div>
  );
}
